import { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import api from "../lib/api";
import { isJoinFeeWaived, isRegistrationSettled } from "../lib/membershipWaiver";
import { membershipStatusBadgeClass } from "../lib/membershipStatusStyle";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Clock, ArrowRight, Receipt, Settings } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Client-side Membership status card — renders on Client Dashboard.
 * Shows registration status + monthly due, with one-click Stripe checkout.
 */
const MembershipCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // "registration" | "monthly" | null
  const [openingPortal, setOpeningPortal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/membership-fees/me");
      setData(r.data);
    } catch (_) {
      // silent — feature degraded if endpoint unavailable
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCheckout = async (paymentType) => {
    setPaying(paymentType);
    try {
      const origin = window.location.origin;
      const r = await api.post("/payments/stripe/checkout", {
        payment_type: paymentType,
        origin_url:   origin,
      });
      // Store session id for polling on return
      try {
        localStorage.setItem("membership_pending_session", r.data.session_id);
        localStorage.setItem("membership_pending_type",    paymentType);
      } catch (_) {}
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed to start");
      setPaying(null);
    }
  };

  const openCustomerPortal = async () => {
    setOpeningPortal(true);
    try {
      const r = await api.post("/membership-fees/customer-portal", {
        return_url: window.location.href,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not open billing portal.");
        setOpeningPortal(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal.");
      setOpeningPortal(false);
    }
  };

  if (loading) {
    return <div className="h-32 rounded-lg bg-slate-100 animate-pulse" data-testid="membership-card-loading" />;
  }
  if (!data) return null;
  if (!data.enabled) return null;

  // STARSEED-CAPMARKETS-069: a founding member's $999 join fee is waived at
  // registration — never render it as due, never gate the monthly on it.
  // Waived is independent of `registration.paid`: the Stripe-paid pioneer
  // path sets registration_paid=True (paid_registration.py:691) even though
  // the member only ever paid the $99 first month, so a paid pioneer must
  // still see "Waived — Founding Member", not "$999 · Paid".
  const regWaived  = isJoinFeeWaived(data);
  const regSettled = isRegistrationSettled(data);
  const allClear   = regSettled && !data.monthly.due;

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="membership-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-blue-600" />
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Membership
            </p>
          </div>
          {allClear ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="membership-status-current">
              <CheckCircle2 size={12} className="mr-1" /> Current
            </Badge>
          ) : (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid="membership-status-due">
              <Clock size={12} className="mr-1" /> Action required
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Registration row */}
          <div className="p-4 rounded-lg border border-slate-200" data-testid="membership-registration-row">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Registration fee</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">
              {regWaived ? "Waived" : fmt(data.registration.amount)}
            </p>
            <div className="mt-3">
              {regWaived ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="membership-registration-waived">
                  <CheckCircle2 size={12} className="mr-1" /> Waived — Founding Member
                </Badge>
              ) : data.registration.paid ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                  <CheckCircle2 size={12} className="mr-1" /> Paid
                </Badge>
              ) : (
                <Button
                  onClick={() => startCheckout("registration")}
                  disabled={paying === "registration"}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="pay-registration-btn"
                >
                  {paying === "registration" ? "Redirecting…" : <>Pay registration <ArrowRight size={12} className="ml-1" /></>}
                </Button>
              )}
            </div>
          </div>

          {/* Monthly row */}
          <div className="p-4 rounded-lg border border-slate-200" data-testid="membership-monthly-row">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Monthly membership</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(data.monthly.amount)}<span className="text-sm font-normal text-slate-500">/mo</span></p>
            <div className="mt-3">
              {!data.monthly.due ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                  <CheckCircle2 size={12} className="mr-1" />
                  Paid {data.monthly.last_paid ? `· through ${new Date(new Date(data.monthly.last_paid).getTime() + 30 * 86400000).toLocaleDateString()}` : ""}
                </Badge>
              ) : (
                <Button
                  onClick={() => startCheckout("monthly")}
                  disabled={paying === "monthly" || !regSettled}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="pay-monthly-btn"
                >
                  {paying === "monthly" ? "Redirecting…" : <>Pay this month <ArrowRight size={12} className="ml-1" /></>}
                </Button>
              )}
              {data.monthly.due && !regSettled && (
                <p className="text-[11px] text-slate-500 mt-2">Pay registration first.</p>
              )}
            </div>
          </div>
        </div>

        {/* History */}
        {data.history?.length > 0 && (
          <details className="mt-4 group" data-testid="membership-history-toggle">
            <summary className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer hover:text-slate-700">
              <Receipt size={12} />
              Payment history ({data.history.length})
            </summary>
            <ul className="mt-3 space-y-1 text-xs">
              {data.history.slice(0, 8).map((h) => (
                <li key={h.id} className="flex items-center justify-between p-2 rounded border border-slate-100" data-testid={`membership-history-${h.id}`}>
                  <span className="text-slate-600 capitalize">{h.fee_type}</span>
                  <span className="font-mono text-slate-900">{fmt(h.amount)}</span>
                  <Badge className={membershipStatusBadgeClass(h.status)}>{h.status}</Badge>
                  <span className="text-slate-400">{h.created_at?.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* Stripe Customer Portal — manage cards / receipts / cancel */}
        {data.registration.paid && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500">
              Update your payment method, view receipts, or manage your subscription.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={openCustomerPortal}
              disabled={openingPortal}
              data-testid="membership-portal-btn"
              className="text-slate-700 hover:text-slate-900 border-slate-300"
            >
              <Settings size={12} className="mr-1.5" />
              {openingPortal ? "Opening…" : "Manage billing"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MembershipCard;
